import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import LoginPage from './components/LoginPage';
import HomeTab from './components/HomeTab';
import RebillTab from './components/RebillTab';
import AutoRbTab from './components/AutoRbTab';
import ClientsTab from './components/ClientsTab';
import RecycleBillingTab from './components/RecycleBillingTab';
import ReportsTab from './components/ReportsTab';
import GatewaysTab from './components/GatewaysTab';
import BlacklistTab from './components/BlacklistTab';
import FriendliesSettingsTab from './components/FriendliesSettingsTab';
import { getClientStats, verifyToken, logout, getToken } from './api';

// In production, Socket.IO connects to the same origin (no explicit URL needed)
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.origin !== 'http://localhost:3000' ? window.location.origin : 'http://localhost:3001');

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [clientStats, setClientStats] = useState({ total: 0, approved: 0, declined: 0 });

  // Check existing token on load
  useEffect(() => {
    const checkAuth = async () => {
      const valid = await verifyToken();
      setAuthenticated(valid);
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  // Listen for auth_expired events (401 from API)
  useEffect(() => {
    const handleExpired = () => { setAuthenticated(false); };
    window.addEventListener('auth_expired', handleExpired);
    return () => window.removeEventListener('auth_expired', handleExpired);
  }, []);

  // Socket.IO connection (only when authenticated)
  useEffect(() => {
    if (!authenticated) return;
    const s = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    setSocket(s);
    return () => { s.disconnect(); };
  }, [authenticated]);

  const fetchStats = useCallback(async () => {
    if (!authenticated) return;
    try {
      const res = await getClientStats();
      if (res.success) setClientStats(res.data);
    } catch (err) { console.error(err); }
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [fetchStats, authenticated]);

  const handleLogin = () => setAuthenticated(true);
  const handleLogout = () => { logout(); setAuthenticated(false); if (socket) socket.disconnect(); };

  // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!authenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const tabs = [
    { key: 'home', label: 'Accueil', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { key: 'rebill', label: 'Rebill', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { key: 'autorb', label: 'AutoRb', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', badgeColor: 'bg-purple-500/20 text-purple-400' },
    { key: 'clients', label: 'Clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', badge: clientStats.approved, badgeColor: 'bg-green-500/20 text-green-400' },
    { key: 'recycle', label: 'Recycle Billing', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', badgeColor: 'bg-orange-500/20 text-orange-400' },
    { key: 'reports', label: 'Reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', badgeColor: 'bg-cyan-500/20 text-cyan-400' },
    { key: 'gateways', label: 'Gateways', icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2' },
    { key: 'blacklist', label: 'Blacklist', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', badgeColor: 'bg-orange-500/20 text-orange-400' },
    { key: 'friendlies', label: '🇺🇸 Friendlies', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z', badgeColor: 'bg-emerald-500/20 text-emerald-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="bg-slate-800 border-b border-slate-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center space-x-3">
              <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h1 className="text-lg font-bold text-white">Rebill Engine</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1.5">
                <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className="text-xs text-slate-400">{connected ? 'Live' : 'Offline'}</span>
              </div>
              <button onClick={handleLogout}
                className="px-3 py-1.5 bg-slate-700 hover:bg-red-600/80 text-slate-300 hover:text-white rounded-lg text-xs transition-colors">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-0.5 overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}>
                <span className="flex items-center space-x-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab.badgeColor || 'bg-blue-500/20 text-blue-400'}`}>{tab.badge}</span>
                  )}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'home' && <HomeTab socket={socket} />}
        {activeTab === 'rebill' && <RebillTab socket={socket} />}
        {activeTab === 'autorb' && <AutoRbTab socket={socket} />}
        {activeTab === 'clients' && <ClientsTab socket={socket} onSync={fetchStats} />}
        {activeTab === 'recycle' && <RecycleBillingTab socket={socket} />}
        {activeTab === 'reports' && <ReportsTab />}
        {activeTab === 'gateways' && <GatewaysTab socket={socket} />}
        {activeTab === 'blacklist' && <BlacklistTab />}
        {activeTab === 'friendlies' && <FriendliesSettingsTab />}
      </main>
    </div>
  );
}

export default App;
